import React from 'react';
import './Footer.css';

interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface SocialMedia {
  platform: string;
  url: string;
}

interface FooterProps {
  signUpText: string;
  signUpButtonText: string;
  signUpButtonLink: string;
  hotels: FooterLink[];
  residences: FooterLink[];
  mediaAndSales: FooterColumn;
  getToKnowUs: FooterColumn;
  socialMedia: SocialMedia[];
  giftsLink: FooterLink;
  contactLink: FooterLink;
  disclaimerLeft: string;
  disclaimerRight: string;
  legalLinks: FooterLink[];
  copyrightText: string;
  developerCredit?: {
    text: string;
    url: string;
  };
}

const Footer: React.FC<FooterProps> = ({
  signUpText,
  signUpButtonText,
  signUpButtonLink,
  hotels,
  residences,
  mediaAndSales,
  getToKnowUs,
  socialMedia,
  giftsLink,
  contactLink,
  disclaimerLeft,
  disclaimerRight,
  legalLinks,
  copyrightText,
  developerCredit,
}) => {
  return (
    <footer className="footer-desktop">
      {/* <div className="logo-wrapper max-width">
        <div className="logo">
          <a href="/" aria-label="Home" className="link-item">
            <img src="/logo.png" alt="The Jaipur Hotels" />
          </a>
        </div>
        <div className="sign-up-wrapper">
          <div className="sign-up-text">
            <p className="sign-up-label">{signUpText}</p>
          </div>
          <div className="sign-up-button-wrapper">
            <div className="vertical-separator"></div>
            <div className="sign-up-button">
              <a
                href={signUpButtonLink}
                aria-label={signUpButtonText}
                className="btn-primary"
              >
                {signUpButtonText}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="horizontal-separator"></div> */}

      <div className="main-data-wrapper max-width">
        <div className="hotels">
          <p className="link-label">Hotels</p>
          {hotels.map((hotel, index) => (
            <a
              key={index}
              href={hotel.url}
              className="link-item"
              aria-label={hotel.label}
              target={hotel.external ? '_blank' : ''}
              rel={hotel.external ? 'noopener noreferrer' : ''}
            >
              {hotel.label}
            </a>
          ))}
        </div>

        <div className="residences">
          <p className="link-label">Residences</p>
          {residences.map((residence, index) => (
            <a
              key={index}
              href={residence.url}
              className="link-item"
              aria-label={residence.label}
              target={residence.external ? '_blank' : ''}
              rel={residence.external ? 'noopener noreferrer' : ''}
            >
              {residence.label}
            </a>
          ))}
        </div>

        <div className="carrier-wrapper">
          <div className="footer-nav-element">
            <p className="link-label">{mediaAndSales.title}</p>
            <div className="nav-link">
              {mediaAndSales.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  aria-label={link.label}
                  target={link.external ? '_blank' : ''}
                  rel={link.external ? 'noopener noreferrer' : ''}
                  className="nav-link-item"
                >
                  {link.label}
                  {link.external && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="arrow"
                    >
                      <path
                        d="M9.52513 5.98969C13.7678 10.2323 17.6569 6.34324 17.6569 6.34324M17.6569 6.34324C17.6569 6.34324 13.7678 10.2323 18.0104 14.475M17.6569 6.34324L6.34315 17.6569"
                        stroke="#8B6C26"
                        strokeLinejoin="bevel"
                      />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>

          <div className="footer-nav-element">
            <p className="link-label">{getToKnowUs.title}</p>
            <div className="nav-link">
              {getToKnowUs.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  aria-label={link.label}
                  target={link.external ? '_blank' : ''}
                  rel={link.external ? 'noopener noreferrer' : ''}
                  className="nav-link-item"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="social-media-wrapper">
          <div className="footer-social-media-element">
            <p className="link-label">Follow us</p>
            <div className="social-icons">
              {socialMedia.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  aria-label={`${social.platform} opens in new window`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                >
                  {social.platform === 'instagram' && (
                    <svg
                      width="28"
                      height="29"
                      viewBox="0 0 28 29"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="social-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M22.709 6.08595C21.7526 5.12954 20.4565 4.59045 19.104 4.58641H8.89602C7.54345 4.59045 6.24744 5.12954 5.29103 6.08595C4.33462 7.04236 3.79552 8.33837 3.79148 9.69094V19.8989C3.79552 21.2515 4.33462 22.5475 5.29103 23.5039C6.24744 24.4603 7.54345 24.9994 8.89602 25.0034H19.104C20.4565 24.9994 21.7526 24.4603 22.709 23.5039C23.6654 22.5475 24.2045 21.2515 24.2085 19.8989V9.69094C24.2045 8.33837 23.6654 7.04236 22.709 6.08595ZM8.89602 2.54492H19.104C23.0344 2.54492 26.25 5.76055 26.25 9.69094V19.8989C26.25 23.8293 23.0344 27.0449 19.104 27.0449H8.89602C4.96562 27.0449 1.75 23.8293 1.75 19.8989V9.69094C1.75 5.76055 4.96562 2.54492 8.89602 2.54492ZM19.7845 9.43291C20.0363 9.60117 20.3324 9.69097 20.6352 9.69097C20.8364 9.6914 21.0357 9.65209 21.2217 9.57529C21.4077 9.49849 21.5767 9.38572 21.7189 9.24344C21.8612 9.10117 21.974 8.93219 22.0508 8.74622C22.1276 8.56024 22.1669 8.36093 22.1665 8.15972C22.1665 7.85687 22.0766 7.56082 21.9084 7.309C21.7401 7.05719 21.501 6.86093 21.2212 6.74503C20.9414 6.62913 20.6335 6.59881 20.3365 6.65789C20.0394 6.71698 19.7666 6.86281 19.5525 7.07696C19.3383 7.29111 19.1925 7.56396 19.1334 7.86099C19.0743 8.15802 19.1046 8.46591 19.2205 8.74571C19.3364 9.02551 19.5327 9.26465 19.7845 9.43291ZM16.2687 11.3996C15.5971 10.9509 14.8076 10.7114 14 10.7114C12.9173 10.7126 11.8793 11.1432 11.1138 11.9088C10.3482 12.6743 9.91764 13.7123 9.91648 14.795C9.91648 15.6026 10.156 16.3921 10.6047 17.0636C11.0534 17.7352 11.6911 18.2586 12.4373 18.5676C13.1835 18.8767 14.0045 18.9576 14.7966 18.8C15.5888 18.6425 16.3164 18.2535 16.8875 17.6824C17.4586 17.1114 17.8475 16.3837 18.005 15.5916C18.1626 14.7995 18.0817 13.9784 17.7727 13.2323C17.4636 12.4861 16.9402 11.8483 16.2687 11.3996ZM10.5971 9.70221C11.6044 9.02918 12.7886 8.66996 14 8.66996C15.6244 8.66996 17.1824 9.31527 18.331 10.4639C19.4797 11.6126 20.125 13.1705 20.125 14.795C20.125 16.0064 19.7658 17.1906 19.0927 18.1978C18.4197 19.2051 17.4631 19.9901 16.3439 20.4537C15.2247 20.9173 13.9932 21.0386 12.8051 20.8023C11.6169 20.5659 10.5256 19.9826 9.66897 19.126C8.81238 18.2694 8.22903 17.178 7.99269 15.9899C7.75636 14.8018 7.87765 13.5702 8.34124 12.451C8.80483 11.3318 9.58988 10.3752 10.5971 9.70221Z"
                        fill="black"
                      />
                    </svg>
                  )}
                  {social.platform === 'facebook' && (
                    <svg
                      width="28"
                      height="29"
                      viewBox="0 0 28 29"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="social-icon"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M26.25 14.8691C26.25 8.10429 20.7648 2.61914 14 2.61914C7.23516 2.61914 1.75 8.10429 1.75 14.8691C1.75 20.9832 6.22891 26.0511 12.0859 26.9709V18.4112H8.97477V14.8691H12.0859V12.1703C12.0859 9.1007 13.9152 7.40375 16.713 7.40375C18.0534 7.40375 19.4556 7.64328 19.4556 7.64328V10.6582H17.9102C16.3893 10.6582 15.9135 11.6021 15.9135 12.5723V14.8691H19.3107L18.7682 18.4112H15.9141V26.972C21.7711 26.0527 26.25 20.9848 26.25 14.8691Z"
                        fill="black"
                      />
                    </svg>
                  )}
                  {social.platform === 'twitter' && (
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="social-icon"
                    >
                      <g>
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </g>
                    </svg>
                  )}
                  {social.platform === 'tiktok' && (
                    <svg
                      width="28"
                      height="29"
                      viewBox="0 0 28 29"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="social-icon"
                    >
                      <path
                        d="M22.3708 8.11907C22.2133 8.03768 22.06 7.94845 21.9115 7.85173C21.4795 7.56613 21.0834 7.22961 20.7317 6.84941C19.8519 5.84273 19.5233 4.82146 19.4023 4.10643H19.4071C19.306 3.51291 19.3478 3.12891 19.3542 3.12891H15.3469V18.6244C15.3469 18.8324 15.3469 19.038 15.3381 19.2412C15.3381 19.2665 15.3357 19.2898 15.3342 19.3171C15.3342 19.3282 15.3342 19.3399 15.3318 19.3516C15.3318 19.3545 15.3318 19.3574 15.3318 19.3603C15.2895 19.9163 15.1113 20.4534 14.8128 20.9243C14.5143 21.3952 14.1046 21.7856 13.6198 22.061C13.1145 22.3485 12.5431 22.4993 11.9617 22.4985C10.0947 22.4985 8.5815 20.9761 8.5815 19.0959C8.5815 17.2157 10.0947 15.6933 11.9617 15.6933C12.3152 15.693 12.6664 15.7486 13.0025 15.8581L13.0073 11.7778C11.9872 11.6461 10.9508 11.7272 9.96362 12.016C8.97641 12.3048 8.05975 12.795 7.2715 13.4558C6.5808 14.0559 6.00013 14.772 5.55561 15.5718C5.38645 15.8634 4.74822 17.0354 4.67093 18.9374C4.62232 20.017 4.94654 21.1355 5.10112 21.5978V21.6075C5.19834 21.8797 5.57505 22.8086 6.18898 23.5917C6.68403 24.2199 7.26891 24.7717 7.9248 25.2293V25.2196L7.93452 25.2293C9.87449 26.5476 12.0254 26.4611 12.0254 26.4611C12.3978 26.446 13.6451 26.4611 15.0615 25.7898C16.6326 25.0456 17.527 23.9368 17.527 23.9368C18.0983 23.2743 18.5527 22.5193 18.8705 21.7042C19.2331 20.751 19.3542 19.6077 19.3542 19.1508V10.9301C19.4028 10.9593 20.0502 11.3875 20.0502 11.3875C20.0502 11.3875 20.983 11.9854 22.4384 12.3748C23.4825 12.6518 24.8892 12.7102 24.8892 12.7102V8.73203C24.3963 8.7855 23.3955 8.62995 22.3708 8.11907Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>

          <div className="footer-nav-element">
            <p className="link-label">Gifts</p>
            <div className="nav-link">
              <a
                href={giftsLink.url}
                aria-label={giftsLink.label}
                target={giftsLink.external ? '_blank' : ''}
                rel={giftsLink.external ? 'noopener noreferrer' : ''}
                className="nav-link-item"
              >
                {giftsLink.label}
                {giftsLink.external && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="arrow"
                  >
                    <path
                      d="M9.52513 5.98969C13.7678 10.2323 17.6569 6.34324 17.6569 6.34324M17.6569 6.34324C17.6569 6.34324 13.7678 10.2323 18.0104 14.475M17.6569 6.34324L6.34315 17.6569"
                      stroke="#8B6C26"
                      strokeLinejoin="bevel"
                    />
                  </svg>
                )}
              </a>
            </div>
          </div>

          <div className="footer-nav-element">
            <p className="link-label">Get in touch</p>
            <div className="nav-link">
              <a
                href={contactLink.url}
                aria-label={contactLink.label}
                target={contactLink.external ? '_blank' : ''}
                rel={contactLink.external ? 'noopener noreferrer' : ''}
                className="nav-link-item"
              >
                {contactLink.label}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="horizontal-separator"></div>

      <div className="disclaimer-wrapper max-width">
        <div className="first-disclaimer">
          <p className="disclaimert-text">{disclaimerLeft}</p>
        </div>
        <div className="vertical-separator hidden md:flex md:h-12"></div>
        <div className="second-disclaimer">
          <p className="disclaimert-text">{disclaimerRight}</p>
        </div>
      </div>

      <div className="horizontal-separator"></div>

      <div className="bottom-text max-width">
        <div className="bottom-link-wrapper">
          {legalLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              aria-label={link.label}
              target={link.external ? '_blank' : ''}
              rel={link.external ? 'noopener noreferrer' : ''}
              className="bottom-link-item"
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="copy-right-text">
          <p className="copy-text">{copyrightText}</p>
          {developerCredit && (
            <p className="developer-credit">
              <a
                href={developerCredit.url}
                target="_blank"
                rel="noopener noreferrer"
                className="developer-link"
              >
                {developerCredit.text}
              </a>
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
